export type DeviceTier = "lean" | "balanced" | "full";

export interface DeviceProfile {
  tier: DeviceTier;
  memoryGb: number | null;
  cores: number | null;
  saveData: boolean;
  effectiveType: string;
  reasons: string[];
}

type NavigatorHints = Navigator & {
  deviceMemory?: number;
  connection?: { saveData?: boolean; effectiveType?: string };
};

type MemoryHints = { usedJSHeapSize?: number; jsHeapSizeLimit?: number };

/** Conservative, synchronous profile used before expensive modules are imported. */
export function deviceProfile(): DeviceProfile {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return { tier: "lean", memoryGb: null, cores: null, saveData: false, effectiveType: "unknown", reasons: ["server"] };
  }
  try {
    const nav = navigator as NavigatorHints;
    const memoryGb = typeof nav.deviceMemory === "number" ? nav.deviceMemory : null;
    const cores = typeof nav.hardwareConcurrency === "number" ? nav.hardwareConcurrency : null;
    const saveData = Boolean(nav.connection?.saveData);
    const effectiveType = nav.connection?.effectiveType ?? "unknown";
    const reasons: string[] = [];
    const shortSide = Math.min(window.innerWidth, window.innerHeight);
    const coarse = window.matchMedia?.("(pointer: coarse)").matches ?? false;
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    const heap = (performance as Performance & { memory?: MemoryHints }).memory;
    const heapPressure = Boolean(heap?.usedJSHeapSize && heap?.jsHeapSizeLimit && heap.usedJSHeapSize / heap.jsHeapSizeLimit > 0.68);

    if (saveData) reasons.push("data-saver");
    if (["slow-2g", "2g", "3g"].includes(effectiveType)) reasons.push(`network-${effectiveType}`);
    if (memoryGb !== null && memoryGb <= 4) reasons.push(`memory-${memoryGb}gb`);
    if (cores !== null && cores <= 4) reasons.push(`cores-${cores}`);
    if (shortSide < 700 && coarse) reasons.push("small-touch-device");
    if (heapPressure) reasons.push("heap-pressure");
    if (reducedMotion) reasons.push("reduced-motion");

    const hardLean = saveData || heapPressure || (memoryGb !== null && memoryGb <= 4) ||
      (cores !== null && cores <= 2) || ["slow-2g", "2g"].includes(effectiveType);
    const tier: DeviceTier = hardLean ? "lean" : reasons.length > 0 ? "balanced" : "full";
    return { tier, memoryGb, cores, saveData, effectiveType, reasons };
  } catch {
    return { tier: "lean", memoryGb: null, cores: null, saveData: false, effectiveType: "unknown", reasons: ["profile-unavailable"] };
  }
}

export function heavyFeaturesAllowed(): boolean {
  return deviceProfile().tier === "full";
}